// GTM-ordered rail navigation — the spine for the role-based redesign
// (docs/redesign/role-based-redesign-plan.html).
//
// P0 rule: ZERO behavior change. Every leaf points at an EXISTING route and
// carries the SAME module gate the flat top-nav (AppShell) used, so a user
// sees exactly the same set of destinations — only their grouping and order
// change (from one flat bar to a funnel-ordered left rail). The module-gating
// helper `canSeeNav` is ported verbatim from AppShell's `canSee`.
//
// Sections are the GTM lifecycle (Acquire → Activate → Engage → Retain →
// Expand → Sell → Prospect). Expand and Prospect have no standalone route on
// `main` today, so they carry no leaves yet and are simply absent — they slot
// in as later phases (expansion lane, the new `prospect` module) without rail
// surgery. The cross-cutting tail (Milestones / Insights / Cohorts / Tools /
// Pulse) sits outside funnel order.

import type { Role } from './auth-types';
import { MARKETING_VIEWS } from './marketing-views';

export interface RailLeaf {
  to: string;
  label: string;
  /** Exact-route match for active highlighting (NavLink `end`). */
  end?: boolean;
  /** Single-module gate. */
  module?: string;
  /** Any-of gate — visible with AT LEAST ONE grant. Takes precedence over `module`. */
  modules?: string[];
  /** Admin-only regardless of module grants. */
  adminOnly?: boolean;
  /**
   * ⌘K-only: searchable in the CommandPalette but NOT rendered as a rail row
   * (legacy spine or "More views"). For in-page views (?view= deep-links) —
   * the rail lists PAGES; the palette searches every VIEW.
   */
  paletteOnly?: boolean;
  /** Extra ⌘K search terms — the view's in-page tab name, synonyms, card
   *  titles — so vocabulary mismatches ("channels" vs "Sources") still hit. */
  keywords?: string[];
}

export interface RailSection {
  key: string;
  label: string;
  /** 1-based position in the 7-stage GTM funnel, shown as "n/7" in the rail. */
  ord: number;
  leaves: RailLeaf[];
}

// The seven GTM-lifecycle sections — the source of truth for the primary nav
// (the old flat AppShell.NAV_ITEMS it was derived from has been removed).
export const RAIL_SECTIONS: RailSection[] = [
  {
    key: 'acquire',
    label: 'Acquire',
    ord: 1,
    leaves: [
      // The Marketing hub (paid-ads command center + its Sources views).
      { to: '/paid-ads', label: 'Marketing', modules: ['paid_ads', 'acquisition'] },
      // ⌘K-only deep-links into EVERY command-center view (PaidAdsPage reads
      // ?view=), derived from the shared manifest so a new/renamed view can't
      // silently miss the palette. paletteOnly keeps them out of the rendered
      // rails — the rail lists pages, the palette searches views.
      ...MARKETING_VIEWS.map(
        (v): RailLeaf => ({
          to: `/paid-ads?view=${v.key}`,
          label: `Marketing · ${v.label}`,
          module: v.module,
          paletteOnly: true,
          keywords: v.keywords,
        }),
      ),
      // The affiliate/partner program — promoted out of the hub's Sources
      // sub-tabs so it's rail-visible (and ⌘K-findable) on its own.
      { to: '/influencers', label: 'Influencers', module: 'acquisition', keywords: ['partners', 'affiliates', 'utm links'] },
    ],
  },
  {
    key: 'activate',
    label: 'Activate',
    ord: 2,
    leaves: [
      // Onboarding — the clear first-time-user funnel + A/B experiment readout.
      // The top-of-funnel north-star the Product team optimizes first.
      // ⌘K matches the RAW query as a substring of these joined terms — it is
      // never tokenized — so the spelling variants ("drop off" / "drop-off")
      // have to be spelled out or the miss is silent.
      {
        to: '/onboarding',
        label: 'Onboarding',
        module: 'activation',
        keywords: [
          'ftue',
          'first time',
          'first-time',
          'journey',
          'video',
          'reel',
          'tour',
          'drop-off',
          'drop off',
          'dropoff',
          'week over week',
          'week on week',
          'wow',
          'trend',
          'day by day',
          'signups over time',
        ],
      },
      { to: '/experiments', label: 'Experiments', module: 'activation' },
      // "Activation Funnel" everywhere — the hub rails already use this name;
      // one destination should not carry two labels.
      { to: '/activation', label: 'Activation Funnel', module: 'activation' },
      // FTUE Replay Insights — watch real first-time journeys at the worst
      // funnel drop-off. Admin-default (PII); granted to the Product persona.
      { to: '/ftue', label: 'FTUE Replays', module: 'ftue' },
    ],
  },
  {
    key: 'engage',
    label: 'Engage',
    ord: 3,
    // Usage is gated by the 'overview' module today (a known coupling).
    leaves: [{ to: '/usage', label: 'Usage', module: 'overview' }],
  },
  {
    key: 'retain',
    label: 'Retain',
    ord: 4,
    leaves: [
      { to: '/retention', label: 'Retention', module: 'retention' },
      { to: '/customers', label: 'Customers', module: 'orgs' },
    ],
  },
  // Expand — expansion lives inside the Pipeline board today; no standalone
  // route on main, so no leaf yet. Slots in as a later phase.
  { key: 'expand', label: 'Expand', ord: 5, leaves: [] },
  {
    key: 'sell',
    label: 'Sell',
    ord: 6,
    leaves: [
      {
        to: '/sales/prospecting',
        label: 'Prospecting',
        module: 'sales',
        keywords: ['assigned leads', 'sequences', 'outbound', 'email', 'call', 'linkedin'],
      },
      { to: '/orgs', label: 'Accounts', module: 'orgs' },
      { to: '/pipeline', label: 'Pipeline', module: 'pipeline' },
      { to: '/contracts', label: 'Contracts', module: 'pipeline' },
      // Forecast — the sales team's weekly quarterly call (AI pre-drafted
      // deal-level calls → immutable weekly submissions; manager roll-up).
      { to: '/forecast', label: 'Forecast', module: 'forecast', keywords: ['commit', 'quota', 'best case'] },
      // Sales Enablement — the live Discovery Call Report tool. Gated on the
      // 'orgs' module (the module every GTM persona holds; admins bypass).
      { to: '/sales-enablement', label: 'Sales Enablement', module: 'orgs', keywords: ['discovery', 'roi', 'pitch', 'brief', 'executive'] },
      {
        to: '/sales/pricing',
        label: 'Pricing',
        module: 'orgs',
        keywords: ['pricing', 'quote', 'discount', 'proposal', 'pack', 'seats', 'list price', 'deck'],
      },
    ],
  },
  // Prospect — the BDR area: the unowned product-qualified-account worklist.
  {
    key: 'prospect',
    label: 'Prospect',
    ord: 7,
    leaves: [{ to: '/prospect', label: 'Queue', module: 'prospect' }],
  },
];

// Cross-cutting tail — primitives every role reaches, outside funnel order.
// Pulse is ungated (the shared scoreboard) and also lives in the ribbon above
// the rail; kept here so it has a durable URL destination too.
export const RAIL_TAIL: RailLeaf[] = [
  {
    to: '/field-plan',
    label: 'Field Plan',
    adminOnly: true,
    keywords: ['global accounts', 'batch', 'targets', 'use cases'],
  },
  { to: '/milestones', label: 'Milestones', module: 'milestones' },
  { to: '/insights', label: 'Insights', module: 'insights' },
  { to: '/cohorts', label: 'Cohorts', module: 'cohorts' },
  { to: '/tools', label: 'Tools', module: 'tools' },
  { to: '/pulse', label: 'Pulse' },
];

// Ported verbatim from AppShell.canSee — same gating semantics so the rail
// shows exactly what the flat nav showed.
export function canSeeNav(item: RailLeaf, role: Role | null, allowedModules: string[]): boolean {
  const isAdmin = role === 'admin';
  if (item.adminOnly && !isAdmin) return false;
  if (!item.module && !item.modules) return true;
  if (isAdmin) return true;
  if (item.modules) return item.modules.some((m) => allowedModules.includes(m));
  return allowedModules.includes(item.module!);
}

// A section is visible when at least one of its leaves is visible. Rendered
// rails skip paletteOnly leaves (they exist for ⌘K search, not as rows).
export function visibleLeaves(
  section: RailSection,
  role: Role | null,
  allowedModules: string[],
): RailLeaf[] {
  return section.leaves.filter((l) => !l.paletteOnly && canSeeNav(l, role, allowedModules));
}
